import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchTodayNewDetailsRequest } from "../../redux/reports/reservationReport/reservationReportActions";
import {
    ComposedChart, Bar, BarChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, ReferenceLine, LabelList,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar, { DATE_PRESETS_DESKTOP, DATE_PRESETS_MOBILE } from "../layout/TopBar";
import Phone from "../Phone";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { getStatusPill } from "../../utils/statusPill";
import "./reports.css";
import "./ReservationReport.scss";
import { T, useIsDesktop, Bone } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import { useSwipeBack } from '../../hooks/useSwipeBack';
import {
    getReservationOverviewTotals,
    getReservationOverviewDailyAndGuests,
    getReservationOverviewDetails,
    getTodaysReservationOverview,
    getTodaysReservationSeated,
    getTodaysReservationAssigned,
    getUpcomingReservationDetails,
    getUpcomingReservationOverview,
    getUpcomingReservationVolumeBySession,
    getReservationTurnTimeAccuracy,
    getReservationChannelDistribution,
    getReservationHourlyCovers,
    getReservationHeatmap,
    getReservationNoShowSummary,
    getReservationAvgWaitTime,
    getReservationSeatingEfficiency,
    getReservationPartySizeDelta,
    todayStr,
    yesterdayStr,
} from "../../services/reportsApi";
import { useStoreTimeZone, getStoreTimeZone, getYesterdayRange, get7DayRange, get30DayRange, getThisMonthRange } from '../../utils/dateTimeUtils';

/*
Legacy shimmer (kept commented as requested):
- Desktop shimmer had 5 KPI cards plus:
  1) Weekly Reservations Trend
  2) Turn Time Accuracy
  3) Reservation Channels
  4) Shift Forecast / Hourly Covers
  5) Reservation Heatmap
- Mobile shimmer had:
  1) KPI grid (3 + 2 cards)
  2) Daily Reservations chart skeleton

function ReservationDesktopShimmer() {
    const kpiLabels = ["TOTAL RESERVATIONS", "TOTAL GUESTS", "TOTAL CANCELLATIONS", "AVG RESERVATIONS", "AVERAGE GUEST SIZE"];
    return (
        <div className="rpt-page">
            <div className="rpt-flex-row rpt-flex-wrap rpt-gap-16">
                {kpiLabels.map((label) => (
                    <div key={label} className="rpt-tile rpt-card resv-shimmer-desktop__tile">
                        <div className="rpt-tile__label resv-shimmer-desktop__tile-label">{label}</div>
                        <Bone h={32} r={6} mb={6} />
                        <Bone h={14} w={80} r={4} />
                    </div>
                ))}
            </div>
            <div className="resv-shimmer-desktop__flex-row">
                <div className="resv-shimmer-desktop__card">
                    <div className="resv-shimmer-desktop__card-title">Weekly Reservations Trend</div>
                    <Bone h={180} r={8} />
                </div>
                <div className="resv-shimmer-desktop__card">
                    <div className="resv-shimmer-desktop__card-title">Turn Time Accuracy</div>
                    <Bone h={180} r={8} />
                </div>
            </div>
            <div className="resv-shimmer-desktop__flex-row">
                <div className="resv-shimmer-desktop__card">
                    <div className="resv-shimmer-desktop__card-title">Reservation Channels</div>
                    <Bone h={160} r={8} />
                </div>
            </div>
            <div className="resv-shimmer-desktop__flex-row">
                <div className="resv-shimmer-desktop__card">
                    <div className="resv-shimmer-desktop__card-title">Shift Forecast / Hourly Covers</div>
                    <Bone h={140} r={8} />
                </div>
            </div>
            <div className="resv-shimmer-desktop__card">
                <div className="resv-shimmer-desktop__card-title">Reservation Heatmap</div>
                <Bone h={120} r={8} />
            </div>
        </div>
    );
}

function ReservationMobileShimmer() {
    const kpiLabels = ["TOTAL RESERVATIONS", "TOTAL GUESTS", "TOTAL CANCELLATIONS", "AVG RESERVATIONS", "AVERAGE GUEST SIZE"];
    return (
        <div className="rpt-flex-col rpt-gap-8 resv-shimmer-mobile">
            <div className="resv-shimmer-mobile__grid-3">
                {kpiLabels.slice(0, 3).map((label) => (
                    <div key={label} className="rpt-card resv-shimmer-mobile__kpi-card">
                        <div className="rpt-card-label-sm rpt-t3 resv-shimmer-mobile__kpi-label">{label}</div>
                        <Bone h={18} r={5} />
                    </div>
                ))}
            </div>
            <div className="resv-shimmer-mobile__grid-2">
                {kpiLabels.slice(3).map((label) => (
                    <div key={label} className="rpt-card resv-shimmer-mobile__kpi-card">
                        <div className="rpt-card-label-sm rpt-t3 resv-shimmer-mobile__kpi-label">{label}</div>
                        <Bone h={18} r={5} />
                    </div>
                ))}
            </div>
            <div className="rpt-card resv-shimmer-mobile__chart-card">
                <div className="rpt-section-title">Daily Reservations</div>
                <Bone h={100} r={8} />
            </div>
        </div>
    );
}
*/
function ReservationDesktopShimmer() {
    return (
        <div className="rpt-page resv-shimmer-desktop">
            <div className="resv-shimmer-desktop__kpi-row">
                <div className="resv-shimmer-desktop__kpi-card">
                    <div className="rpt-tile__label resv-shimmer-desktop__tile-label">TOTAL RESERVATIONS</div>
                    <Bone h={34} r={6} mb={8} />
                    <Bone h={12} w={84} r={4} />
                </div>
            </div>

            <div className="resv-shimmer-desktop__flex-row">
                <div className="resv-shimmer-desktop__card resv-shimmer-desktop__card--channel">
                    <div className="resv-shimmer-desktop__card-title">Reservation Channels</div>
                    <div className="resv-shimmer-desktop__donut-row">
                        <Bone h={134} w={134} r={999} />
                        <div className="resv-shimmer-desktop__legend-col">
                            {[1, 2, 3, 4].map((i) => (
                                <Bone key={i} h={12} r={4} />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="resv-shimmer-desktop__card resv-shimmer-desktop__card--forecast">
                    <div className="resv-shimmer-desktop__card-title">Shift Forecast</div>
                    <Bone h={160} r={8} />
                    <div className="resv-shimmer-desktop__stats-row">
                        <Bone h={12} w={110} r={4} />
                        <Bone h={12} w={70} r={4} />
                        <Bone h={12} w={80} r={4} />
                    </div>
                </div>
            </div>

            <div className="resv-shimmer-desktop__card">
                <div className="resv-shimmer-desktop__card-title">Reservation Heatmap</div>
                <div className="resv-shimmer-desktop__heatmap-grid">
                    {[1, 2, 3, 4, 5, 6, 7].map((r) => (
                        <div key={r} className="resv-shimmer-desktop__heatmap-row">
                            {Array.from({ length: 11 }).map((_, c) => (
                                <Bone key={`${r}-${c}`} h={18} r={3} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <div className="resv-shimmer-desktop__card">
                <div className="resv-shimmer-desktop__table-head">
                    <Bone h={14} w={160} r={4} />
                    <Bone h={32} w={220} r={8} />
                </div>
                <div className="resv-shimmer-desktop__table-rows">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Bone key={i} h={16} r={4} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function ReservationMobileShimmer() {
    return (
        <div className="rpt-flex-col rpt-gap-8 resv-shimmer-mobile">
            <div className="rpt-card resv-shimmer-mobile__kpi-card">
                <div className="rpt-card-label-sm rpt-t3 resv-shimmer-mobile__kpi-label">TOTAL RESERVATIONS</div>
                <Bone h={20} r={5} />
            </div>

            <div className="rpt-card resv-shimmer-mobile__chart-card">
                <div className="rpt-section-title">Reservation Channels</div>
                <Bone h={132} r={8} />
            </div>

            <div className="rpt-card resv-shimmer-mobile__chart-card">
                <div className="rpt-section-title">Shift Forecast</div>
                <Bone h={120} r={8} />
            </div>

            <div className="rpt-card resv-shimmer-mobile__chart-card">
                <div className="rpt-section-title">Reservation Table</div>
                <div className="resv-shimmer-mobile__rows">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Bone key={i} h={14} r={4} />
                    ))}
                </div>
            </div>
        </div>
    );
}
const CHANNEL_COLORS = [T.accent, "#E86B6B", T.orange, T.t3, T.green];
function maskPhone(phone: string): string {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    return `(XXX) XXX-${digits.slice(-4)}`;
}

/* Today's view: filter switches between Upcoming (assigned-details) and Seated (seated-details) */
const TODAY_STATUS_OPTIONS = ["Upcoming", "Seated"];
/* Past/future overview: filter on the overview-details or upcoming-details API */
const OVERVIEW_STATUS_OPTIONS = ["All", "Upcoming", "Assigned", "Seated", "Completed", "Cancelled", "Late Show", "No Show"];

/* Columns differ for today vs past/future */
const TABLE_HEADERS_TODAY = [
    { key: "checkInNumber", label: "Resv ID", sortable: true, width: "0.7fr" },
    { key: "guestName", label: "Guest name", sortable: true, width: "1.3fr" },
    { key: "phone", label: "Phone", sortable: false, width: "1.2fr" },
    { key: "channel", label: "Channel", sortable: true, width: "0.8fr" },
    { key: "resvTime", label: "Resv time", sortable: true, width: "0.9fr" },
    { key: "specialRequest", label: "Special request", sortable: false, width: "1fr" },
    { key: "status", label: "Status", sortable: true, width: "0.9fr" },
    { key: "guestSize", label: "Guest size", sortable: true, width: "0.9fr", align: "center" },
    { key: "tableName", label: "Table", sortable: true, width: "0.7fr" },
];

const TABLE_HEADERS_OVERVIEW = [
    { key: "checkInNumber", label: "Resv ID", sortable: true, width: "0.8fr" },
    { key: "guestName", label: "Guest name", sortable: true, width: "1.5fr" },
    { key: "phone", label: "Phone", sortable: false, width: "1.2fr" },
    { key: "channel", label: "Channel", sortable: true, width: "0.8fr" },
    { key: "resvTime", label: "Resv date & time", sortable: true, width: "1.5fr" },
    { key: "specialRequest", label: "Special request", sortable: false, width: "1fr" },
    { key: "status", label: "Status", sortable: true, width: "0.9fr" },
    { key: "guestSize", label: "Guest size", sortable: true, width: "0.9fr", align: "center" },
    { key: "tableName", label: "Table", sortable: true, width: "0.7fr" },
];

const KPI_KEYS_PAST = [
    { key: "totalReservations", label: "TOTAL RESERVATIONS" },
    { key: "totalGuests", label: "TOTAL GUESTS" },
    { key: "totalCancellation", label: "TOTAL CANCELLATIONS" },
    { key: "averageReservationsPerDay", label: "AVG RESERVATIONS" },
    { key: "averageGuestsPerDay", label: "AVERAGE GUEST SIZE" },
];

const KPI_KEYS_FUTURE = [
    { key: "totalReservation", label: "TOTAL RESERVATIONS" },
    { key: "avgReservation", label: "AVG RESERVATIONS" },
    { key: "avgGuestSize", label: "AVG GUEST SIZE" },
    { key: "cancelled", label: "CANCELLED" },
];

/* ═══════════════════════════════════════════════════════════════════════════
   HOOKS & UTILS
   ═══════════════════════════════════════════════════════════════════════════ */
const findToken = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["accessToken", "access_token", "token", "jwtToken", "jwt", "bearerToken", "authToken", "userToken", "sessionToken", "Authorization", "authorization", "idToken", "id_token"]) {
        if (obj[key] && typeof obj[key] === "string" && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === "string" && val.length > 50) return val;
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["locationId", "location_id", "locationID", "locId", "outletId"]) {
        if (obj[key] && typeof obj[key] === "string") return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrapRes = (res: any) => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const pick = (res: any, ...keys: any[]) => {
    if (res == null) return null;
    for (const key of keys) { const v = res?.[key]; if (v !== undefined && v !== null) return v; }
    const r = unwrapRes(res);
    for (const key of keys) { const v = r?.[key]; if (v !== undefined && v !== null) return v; }
    return null;
};

const num = (v: any) => Number(v ?? 0) || 0;
const fmtNum = (v: any) => num(v).toLocaleString("en-US");

// Historical presets are anchored on the store-timezone anchor date (5 AM cutoff),
// not the raw `yesterday` arg — see getAnchorDate / DateProdIssue.md.
const getStartEndFromFilter = (filter: any, today: any, yesterday: any, timeZoneCd: string = getStoreTimeZone()) => {
    const raw = typeof filter === "string" ? filter.trim() : "";
    const normalized = raw.toLowerCase().replace(/\s+/g, "");
    if (raw && raw.startsWith('custom:')) {
        const p = filter.split(':');
        if (p.length === 3) return { startDate: p[1], endDate: p[2] };
    }
    if (normalized === "today") return { startDate: today, endDate: today };
    if (normalized === "yesterday") return getYesterdayRange(timeZoneCd);
    if (normalized === "7d") return get7DayRange(timeZoneCd);
    if (normalized === "30d") return get30DayRange(timeZoneCd);
    if (normalized === "thismonth") return getThisMonthRange(timeZoneCd);
    return null;
};

/** Determine date context: "today", "past", or "future" */
const getDateContext = (dateRange: any, today: any, activeFilter: any) => {
    const normalized = String(activeFilter || "").trim().toLowerCase().replace(/\s+/g, "");
    if (normalized === "today") return "today";
    if (dateRange.startDate > today) return "future";
    return "past";
};

const isSingleDay = (dr: any) => dr.startDate === dr.endDate;

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

function TruncatedCell({ value, className }: { value: string; className?: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [truncated, setTruncated] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (ref.current) setTruncated(ref.current.scrollWidth > ref.current.offsetWidth);
    }, [value]);

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (!truncated) return;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
        setVisible(true);
    };

    return (
        <div className={className} onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)}>
            <div ref={ref} className="resv-truncated-text">
                {value}
            </div>
            {visible && (
                <div className="resv-truncated-tooltip" style={{ top: pos.y, left: pos.x }}>
                    {value}
                </div>
            )}
        </div>
    );
}

function Card({ children, style, mobile }: any) {
    return (
        <div className={`resv-card${mobile ? " resv-card--mobile" : ""}`} style={style}>
            {children}
        </div>
    );
}

function MTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="resv-tooltip">
            <div className="resv-tooltip__label">{label}</div>
            {payload.map((p, i) => (
                <div key={i} className="resv-tooltip__entry" style={{ color: p.color || T.t2 }}>
                    {p.name}: <span className="resv-tooltip__entry-value">{p.value}</span>
                </div>
            ))}
        </div>
    );
}

function StatusBadge({ status }: any) {
    const raw = String(status ?? "").trim();
    const s = getStatusPill(raw);
    return (
        <span className="resv-status-badge" style={{ background: s.bg, color: s.color }}>
            {s.label}
        </span>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI TILES — matching reference design
   ═══════════════════════════════════════════════════════════════════════════ */

function KPIMobile({ label, value, delta }: any) {
    const hasDelta = delta !== null && delta !== undefined;
    const pos = hasDelta && delta >= 0;
    const dColor = hasDelta ? (pos ? T.green : T.red) : null;
    return (
        <div className="resv-kpi-mobile" style={{
            borderRadius: hasDelta ? 12 : 10,
            padding: hasDelta ? "12px 10px" : 10,
        }}>
            <div className="resv-kpi-mobile__label">{label}</div>
            <div className="resv-kpi-mobile__value" style={{ marginTop: hasDelta ? 4 : 3 }}>{value}</div>
            {hasDelta && (
                <div className="resv-kpi-mobile__delta" style={{ color: dColor }}>
                    {pos ? "\u2191" : "\u2193"} {pos ? "+" : ""}{delta}%
                </div>
            )}
        </div>
    );
}

function KPIDesktop({ label, value, delta, emoji }: any) {
    const hasDelta = delta !== null && delta !== undefined;
    const pos = hasDelta && delta >= 0;
    const dColor = hasDelta ? (pos ? T.green : T.red) : null;
    const blurColor = hasDelta ? (pos ? T.greenSoft : T.redSoft) : T.accentSoft;
    return (
        <div className="resv-kpi-desktop">
            <div className="resv-kpi-desktop__blur" style={{ background: blurColor }} />
            <div className="resv-kpi-desktop__header">
                <div className="resv-kpi-desktop__label">{label}</div>
                {emoji && <span className="resv-kpi-desktop__emoji">{emoji}</span>}
            </div>
            <div className="resv-kpi-desktop__value">{value}</div>
            {hasDelta && (
                /* GLOBAL-DP-4 (ZAR-1663): trailing comparison text removed \u2014 the basis is
                   stated once in the unified date control. */
                <div className="resv-kpi-desktop__delta" title="Compared with the prior period" style={{ color: dColor }}>
                    {pos ? "\u2191" : "\u2193"} {pos ? "+" : ""}{delta}%
                </div>
            )}
        </div>
    );
}

function SecTitle({ children, sub, mobile }: any) {
    return (
        <div style={{ marginBottom: mobile ? 8 : 12 }}>
            <div className="resv-sec-title__heading" style={{ fontSize: mobile ? 12 : 14 }}>{children}</div>
            {sub && <div className="resv-sec-title__sub" style={{ fontSize: mobile ? 9 : 11 }}>{sub}</div>}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DAILY RESERVATIONS & GUESTS CHART
   ═══════════════════════════════════════════════════════════════════════════ */

function DailyChart({ data, mobile }: any) {
    if (!data || data.length === 0) return null;
    return (
        <Card mobile={mobile}>
            <div style={{ marginBottom: mobile ? 8 : 12 }}>
                <div className="resv-daily-chart__title" style={{ fontSize: mobile ? 12 : 14 }}>Daily Reservations & Guests</div>
            </div>
            <div style={{ height: mobile ? 180 : 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} barGap={2}>
                        <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 8 : 10, fill: T.t3, fontFamily: T.font }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: T.t3 }} />
                        <Tooltip content={<MTooltip />} />
                        <Bar dataKey="totalGuests" name="Guests" fill="rgba(210,58,59,0.25)" radius={[4, 4, 0, 0]} maxBarSize={mobile ? 24 : 36} />
                        <Line type="monotone" dataKey="totalReservations" name="Reservations" stroke={T.accent} strokeWidth={2.5} dot={{ r: 3, fill: T.accent }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
}

/* Mobile fallback: simple table for chart data */
function DailyChartMobileTable({ data }: any) {
    const [page, setPage] = useState(0);
    const perPage = 5;
    const totalPages = Math.ceil((data?.length || 0) / perPage);
    const pageData = (data || []).slice(page * perPage, (page + 1) * perPage);

    if (!data || data.length === 0) return null;
    return (
        <Card mobile>
            <div className="resv-daily-table__title">
                Daily Reservations & Guests ({data.length})
            </div>
            <div className="resv-daily-table__grid">
                {["Day", "Reservations", "Guests"].map(h => (
                    <div key={h} className="resv-daily-table__header-cell">{h}</div>
                ))}
                {pageData.map((r, i) => (
                    [r.day, r.totalReservations, r.totalGuests].map((v, ci) => (
                        <div key={`${i}-${ci}`} className="resv-daily-table__cell">{v}</div>
                    ))
                ))}
            </div>
            {totalPages > 1 && (
                <div className="rpt-flex-row rpt-flex-center rpt-gap-12" style={{ justifyContent: "center", marginTop: 8 }}>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="resv-pagin-btn" style={{ opacity: page === 0 ? 0.3 : 1 }}>
                        <span className="material-symbols-rounded resv-pagin-icon">chevron_left</span>
                    </button>
                    <span className="resv-daily-table__pagin-info">{page + 1} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="resv-pagin-btn" style={{ opacity: page >= totalPages - 1 ? 0.3 : 1 }}>
                        <span className="material-symbols-rounded resv-pagin-icon">chevron_right</span>
                    </button>
                </div>
            )}
        </Card>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   RESERVATION DETAILS TABLE
   ═══════════════════════════════════════════════════════════════════════════ */

// paginBtnStyle moved to .resv-pagin-btn in ReservationReport.scss

function ReservationTable({
    title, rows, totalElements, totalPages, page, pageSize, onPageChange, onPageSizeChange,
    search, onSearchChange, statusFilter, onStatusFilterChange, statusOptions,
    sortBy, sortAsc, sortTouched, onSort, loading, mobile, headers, phoneVisible, onTogglePhone,
    error, onRetry,
}: any) {
    const gridCols = headers.map(h => h.width).join(" ");
    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, totalElements || 0);
    // Today pills are shown inline (both desktop and mobile); overview pills only on mobile
    const isTodayPills = statusOptions?.length === 2;

    const sortedRows = rows;

    return (
        <Card mobile={mobile} style={{ overflow: "hidden" }}>
            {/* Header */}
            <div className={`resv-table__header ${mobile ? "resv-table__header--mobile" : "resv-table__header--desktop"}`}>
                <div className={`resv-table__title ${mobile ? "resv-table__title--mobile" : "resv-table__title--desktop"}`}>
                    {title}
                    {totalElements != null && (
                        <span className="resv-table__count-badge">
                            {totalElements}
                        </span>
                    )}
                </div>
                <div className="rpt-flex-row rpt-flex-center rpt-flex-wrap rpt-gap-8">
                    {/* Search */}
                    <div className="rpt-flex-row rpt-flex-center resv-table__search-wrap">
                        <span className="material-symbols-rounded resv-table__search-icon">search</span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={mobile ? "Search..." : "Search by resv ID, customer name, phone number"}
                            className={`resv-table__search-input ${mobile ? "resv-table__search-input--mobile" : "resv-table__search-input--desktop"}`}
                        />
                    </div>
                    {/* Desktop status filter dropdown — commented out for now */}
                    {/* {!mobile && statusOptions && !isTodayPills && (
                        <StatusFilterDropdown value={statusFilter} onChange={onStatusFilterChange} options={statusOptions} />
                    )} */}
                </div>
            </div>

            {/* Error state — shown inside the card so the title stays visible */}
            {error && (
                <SectionError kpiName={title} onRetry={onRetry} size="medium" minHeight={200} />
            )}

            {/* Status filter pills — commented out for now */}
            {/* {statusOptions && (mobile || isTodayPills) && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {statusOptions.map(opt => {
                        const isActive = statusFilter === opt.value || (!statusFilter && opt.value === "");
                        return (
                            <button key={opt.label} onClick={() => onStatusFilterChange(opt.value)} style={{
                                border: `1px solid ${isActive ? T.accent : T.cardBorder}`,
                                borderRadius: 16, padding: "4px 12px", fontSize: 10, fontWeight: 500,
                                background: isActive ? T.accentSoft : T.surface,
                                color: isActive ? T.accent : T.t2,
                                cursor: "pointer", fontFamily: T.font,
                                display: "flex", alignItems: "center", gap: 4,
                            }}>
                                {opt.label}
                                {opt.count != null && (
                                    <span style={{
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                        minWidth: 18, height: 18, borderRadius: 9, padding: "0 5px",
                                        fontSize: 9, fontWeight: 700,
                                        background: isActive ? T.accent : T.t3,
                                        color: "#fff",
                                    }}>
                                        {opt.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )} */}

            {/* Table */}
            {!error && <div className="resv-table__scroll">
                <div className={mobile ? "resv-table__min-width-mobile" : ""}>
                    {/* Column headers */}
                    <div className="resv-table__col-header" style={{ gridTemplateColumns: gridCols }}>
                        {headers.map(h => (
                            <div
                                key={h.key}
                                onClick={h.sortable ? () => onSort(h.key) : undefined}
                                className={`resv-table__col-label ${h.sortable ? "resv-table__col-label--sortable" : ""} ${h.align === "right" ? "resv-table__col-label--right" : h.align === "center" ? "resv-table__col-label--center" : ""}`}
                                style={{ cursor: h.sortable || h.key === "phone" ? "pointer" : "default" }}
                            >
                                {h.label}
                                {h.key === "phone" && (
                                    <span
                                        onClick={(e) => { e.stopPropagation(); onTogglePhone(); }}
                                        className="material-symbols-rounded"
                                        style={{ fontSize: 16, color: T.t3, marginLeft: 4, cursor: "pointer", verticalAlign: "middle" }}
                                    >
                                        {phoneVisible ? "visibility" : "visibility_off"}
                                    </span>
                                )}
                                {h.sortable && (
                                    <span className="resv-table__sort-icon" style={{ color: sortTouched && sortBy === h.key ? T.accent : T.t3, opacity: sortTouched && sortBy === h.key ? 1 : 0.4 }}>
                                        {sortTouched && sortBy === h.key ? (sortAsc ? "↑" : "↓") : "⇅"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Rows */}
                    {loading ? (
                        <div>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="resv-table__row" style={{ gridTemplateColumns: gridCols }}>
                                    {headers.map(h => (
                                        <Bone key={h.key} h={14} r={4} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (!sortedRows || sortedRows.length === 0) ? (
                        <SectionEmpty kpiName="reservations" size="medium" minHeight={200} />
                    ) : (
                        sortedRows.map((row, ri) => (
                            <div key={ri} className="resv-table__row" style={{ gridTemplateColumns: gridCols }}>
                                {headers.map(h => {
                                    const val = row[h.key] ?? "";
                                    if (h.key === "checkInNumber") return <div key={h.key} className="resv-table__cell-id">#{val || row.id || ""}</div>;
                                    if (h.key === "guestName") return <div key={h.key} className="resv-table__cell-name">{val || row.customerName || ""}</div>;
                                    if (h.key === "phone") {
                                        const phone = val || row.phoneNumber || "";
                                        return <div key={h.key} className="resv-table__cell-phone">{phoneVisible ? phone : maskPhone(phone)}</div>;
                                    }
                                    if (h.key === "channel") return <div key={h.key} className="resv-table__cell-channel">{val || row.source || ""}</div>;
                                    if (h.key === "resvTime") return <div key={h.key} className="resv-table__cell-time">{val || row.reservationTime || row.reservationDate || ""}</div>;
                                    if (h.key === "status") return <div key={h.key}><StatusBadge status={val || row.statuses || ""} /></div>;
                                    if (h.key === "guestSize") return <div key={h.key} className="resv-table__cell-size">{val || row.partySize || ""}</div>;
                                    if (h.key === "tableName") { const tableVal = val || row.table || ""; return <TruncatedCell key={h.key} value={tableVal} className="resv-table__cell-table" />; }
                                    if (h.key === "specialRequest" || h.key === "notes") return <div key={h.key} className="resv-table__cell-request">{val || "—"}</div>;
                                    return <div key={h.key} className="resv-table__cell-default">{val}</div>;
                                })}
                            </div>
                        ))
                    )}
                </div>
            </div>}

            {/* Pagination */}
            {!error && totalElements > 0 && (
                <div className="rpt-flex-row rpt-flex-between rpt-flex-center resv-table__pagination">
                    <span className="resv-table__page-info">{start}-{end} of {totalElements}</span>
                    <div className="rpt-flex-row rpt-flex-center rpt-gap-8">
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="resv-table__page-select"
                        >
                            {(mobile ? [5, 10, 20] : [10, 20, 50]).map(n => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                        <button onClick={() => onPageChange(page - 1)} disabled={page <= 0} className="resv-pagin-btn" style={{ opacity: page <= 0 ? 0.3 : 1 }}>
                            <span className="material-symbols-rounded resv-pagin-icon--lg">chevron_left</span>
                        </button>
                        <span className="resv-table__page-num">{page + 1} / {totalPages || 1}</span>
                        <button onClick={() => onPageChange(page + 1)} disabled={page >= (totalPages || 1) - 1} className="resv-pagin-btn" style={{ opacity: page >= (totalPages || 1) - 1 ? 0.3 : 1 }}>
                            <span className="material-symbols-rounded resv-pagin-icon--lg">chevron_right</span>
                        </button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function StatusFilterDropdown({ value, onChange, options }: any) {
    const [open, setOpen] = useState(false);
    const ref = useRef<any>(null);

    useEffect(() => {
        const handler = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} className="resv-dropdown">
            <button onClick={() => setOpen(!open)} className="resv-dropdown__trigger">
                {options.find(o => o.value === value)?.label || "All"} <span className="resv-dropdown__caret">▾</span>
            </button>
            {open && (
                <div className="resv-dropdown__menu">
                    {options.map(opt => {
                        const isActive = value === opt.value || (!value && opt.value === "");
                        return (
                            <div key={opt.label} onClick={() => { onChange(opt.value); setOpen(false); }}
                                className={`resv-dropdown__option ${isActive ? "resv-dropdown__option--active" : "resv-dropdown__option--inactive"}`}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DATA HOOK — routes to correct API based on date context
   ═══════════════════════════════════════════════════════════════════════════ */

function useReservationData(locationId: any, token: any, dateRange: any, tableParams: any, dateContext: any) {
    const [kpiLoading, setKpiLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [error, setError] = useState("");
    const [kpis, setKpis] = useState<any>(null);
    const [chartData, setChartData] = useState([]);
    const [tableData, setTableData] = useState({ content: [], totalPages: 0, totalElements: 0 });
    const [seatedTotal, setSeatedTotal] = useState<any>(null); // for today: seated count to show alongside
    const [chartError, setChartError] = useState(false);
    const [tableError, setTableError] = useState(false);
    const [chartRetryTick, setChartRetryTick] = useState(0);
    const [tableRetryTick, setTableRetryTick] = useState(0);
    const retryChart = () => setChartRetryTick((n) => n + 1);
    const retryTable = () => setTableRetryTick((n) => n + 1);

    // KPIs + Chart — use different APIs for future vs past/today
    useEffect(() => {
        if (!locationId || !token) { setKpiLoading(false); return; }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        setKpiLoading(true);
        setError("");

        // Pick the right API based on date context
        const totalsApi = dateContext === "future"
            ? getUpcomingReservationOverview(opts)
            : dateContext === "today"
                ? getTodaysReservationOverview({ locationId: opts.locationId, token: opts.token })
                : getReservationOverviewTotals(opts);

        const chartApi = dateContext === "future"
            ? getUpcomingReservationVolumeBySession(opts)
            : getReservationOverviewDailyAndGuests(opts);

        setChartError(false);

        Promise.allSettled([
            totalsApi,
            chartApi,
        ]).then(([totalsRes, dailyRes]) => {
            const totals = totalsRes.status === "fulfilled" ? totalsRes.value : null;
            const daily = dailyRes.status === "fulfilled" ? dailyRes.value : null;

            if (totalsRes.status === "rejected") {
                setError(totalsRes.reason?.message || "Failed to load reservations");
            }

            setKpis(unwrapRes(totals));

            const dData = unwrapRes(daily);
            let chartArr = [];
            if (dData) {
                const raw = dData?.content ?? dData?.data ?? (Array.isArray(dData) ? dData : null);
                if (Array.isArray(raw)) {
                    chartArr = raw.map(item => ({
                        day: item.day ?? item.date ?? "",
                        totalReservations: num(item.totalReservations ?? item.reservationCount ?? item.reservations),
                        totalGuests: num(item.totalGuests ?? item.guestCount ?? item.guests),
                    }));
                }
            }
            setChartData(chartArr);
            setChartError(dailyRes.status === "rejected");
            setKpiLoading(false);
        });
    }, [locationId, token, dateRange.startDate, dateRange.endDate, dateContext, chartRetryTick]);

    // Helper: extract paginated response — reads pagination fields from raw response before unwrapping
    const extractPageData = (res: any) => {
        if (res == null) return { content: [], totalPages: 0, totalElements: 0 };
        // The raw API response may be wrapped: { metaDataInfo: { data: { content, totalPages, ... } } }
        // or direct: { content, totalPages, totalElements, numberOfElements, ... }
        const raw = res?.metaDataInfo?.data ?? res?.data ?? res;
        const content = raw?.content ?? (Array.isArray(raw) ? raw : []);
        // Today's APIs may only have numberOfElements as the total count
        const totalElements = raw?.totalElements ?? raw?.numberOfElements ?? (Array.isArray(content) ? content.length : 0);
        const totalPages = raw?.totalPages ?? Math.max(1, Math.ceil(totalElements / (tableParams.size || 10)));
        return { content: Array.isArray(content) ? content : [], totalPages, totalElements };
    };

    // Fetch seated total for today (so we can show it in the Seated pill)
    useEffect(() => {
        if (dateContext !== "today" || !locationId || !token) { setSeatedTotal(null); return; }
        getTodaysReservationSeated({ locationId, token, page: 0, size: 1 })
            .then(res => {
                const raw = res?.metaDataInfo?.data ?? res;
                setSeatedTotal(raw?.totalElements ?? raw?.numberOfElements ?? null);
            })
            .catch(() => setSeatedTotal(null));
    }, [locationId, token, dateContext]);

    // Table — different API based on dateContext + statusFilter
    useEffect(() => {
        if (!locationId || !token) { setTableLoading(false); return; }
        const { page, size, search, statusFilter, sortBy, sortAsc, sortTouched } = tableParams;

        setTableLoading(true);
        setTableError(false);

        let apiCall;

        if (dateContext === "today") {
            // Today's table is handled via Redux (fetchTodayNewDetailsRequest) — skip here
            setTableLoading(false);
            setTableData({ content: [], totalPages: 0, totalElements: 0 });
            return;
        } else if (dateContext === "future") {
            const opts: any = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token, page, size };
            if (search) opts.search = search;
            if (statusFilter) opts.filter = statusFilter;
            apiCall = getUpcomingReservationDetails(opts);
        } else {
            const opts: any = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token, page: page + 1, size };
            if (search) opts.search = search;
            if (statusFilter) opts.filter = statusFilter;
            if (sortTouched) opts.sortBy = sortBy;
            if (sortTouched) opts.sortAsc = sortAsc;
            apiCall = getReservationOverviewDetails(opts);
        }

        apiCall
            .then(res => { setTableData(extractPageData(res)); setTableError(false); })
            .catch(() => { setTableData({ content: [], totalPages: 0, totalElements: 0 }); setTableError(true); })
            .finally(() => setTableLoading(false));
    }, [locationId, token, dateRange.startDate, dateRange.endDate, dateContext, tableParams, tableRetryTick]);

    return { kpiLoading, tableLoading, error, kpis, chartData, tableData, seatedTotal, chartError, tableError, retryChart, retryTable };
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANALYTICS SECTION — Turn Time, Channels, Hourly Covers, Heatmap, etc.
   ═══════════════════════════════════════════════════════════════════════════ */
const DONUT_COLORS = [T.accent, "#E86B6B", T.orange, T.t3, T.blue, T.green, "#8b5cf6", "#ec4899"];
const DAY_NAMES = ["", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WEEKLY_LEGEND = [
    { label: "Reservations", color: T.accent },
    { label: "Guests", color: "rgba(220,38,38,0.5)" },
];

function ATooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    const colorMap: Record<string, string> = { Reservations: T.accent, Guests: "rgba(220,38,38,0.7)" };
    return (
        <div className="resv-tooltip">
            <div className="resv-tooltip__label">{label}</div>
            {payload.map((p, i) => (
                <div key={i} className="resv-tooltip__entry" style={{ color: colorMap[p.name] || p.color || T.t2 }}>
                    {p.name}: <span className="resv-tooltip__entry-value">{p.value}</span>
                </div>
            ))}
        </div>
    );
}

function AnalyticsSection({ analytics, weeklyData, mobile, dateContext }: any) {
    const { turnTime, channels, hourlyCovers, heatmap, noShow, waitTime, seating, partySize } = analytics;
    const hasData = (arr: any) => Array.isArray(arr) && arr.length > 0;

    /* ── Derived chart data ── */
    const ttData = (turnTime || []).map(d => {
        const h = d.hourOfDay ?? 0;
        const suffix = h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`;
        return { hour: suffix, actual: Number(d.actualMinutes ?? 0), estimated: Number(d.estimatedMinutes ?? 0) };
    });

    const chTotal = (channels || []).reduce((s, c) => s + Number(c.reservationCount ?? 0), 0) || 1;
    const channelData = (channels || []).map((c, i) => ({
        name: c.channelName ?? "Unknown",
        value: Number(c.reservationCount ?? 0),
        color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));

    const hcData = (hourlyCovers || []).map(d => {
        const h = d.hourOfDay ?? 0;
        const suffix = h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`;
        return { hour: suffix, covers: Number(d.totalCovers ?? 0) };
    });
    const peakHc = hcData.length ? hcData.reduce((m, x, i, a) => x.covers > a[m].covers ? i : m, 0) : -1;
    const peakHcValue = peakHc >= 0 ? Number(hcData[peakHc].covers) || 0 : 0;
    const activeHc = hcData.filter(d => Number(d.covers) > 0);
    const avgHc = activeHc.length > 0 ? activeHc.reduce((s, d) => s + Number(d.covers), 0) / activeHc.length : 0;
    const totalHc = hcData.reduce((s, d) => s + (Number(d.covers) || 0), 0);
    const showAvgHc = avgHc > 0 && activeHc.length >= 2 && avgHc < peakHcValue;
    const avgHcDisplay = avgHc >= 10 ? Math.round(avgHc).toLocaleString() : avgHc.toFixed(1);

    /* Weekly trend from daily data */
    const weeklyTrend = (weeklyData || []).map(d => ({
        day: d.day ?? d.date ?? "",
        reservations: Number(d.totalReservations ?? 0),
        guests: Number(d.totalGuests ?? d.guests ?? 0),
    }));
    const peakWeekly = weeklyTrend.length ? weeklyTrend.reduce((m, x, i, a) => x.reservations > a[m].reservations ? i : m, 0) : -1;

    /* Heatmap grid */
    const hmGrid: any = {};
    (heatmap || []).forEach((cell: any) => { hmGrid[`${cell.dayOfWeek}-${cell.hourOfDay}`] = Number(cell.reservationCount ?? 0); });
    const hmMax = Math.max(1, ...Object.values(hmGrid) as number[]);
    const hmHours = Array.from(new Set((heatmap || []).map((c: any) => c.hourOfDay))).sort((a: any, b: any) => a - b);
    const hmHourLabels = hmHours.map((h: any) => h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`);
    const hmDays = [2, 3, 4, 5, 6, 7, 1]; // Mon-Sun

    /* Seating / Floor data */
    const totalSeated = Number(seating?.totalSeated ?? 0);
    const totalCapacity = Number(seating?.totalCapacity ?? 0);
    const seatPct = totalCapacity > 0 ? Math.round((totalSeated / totalCapacity) * 100) : Number(seating?.efficiencyPercent ?? 0);
    const floorSegs = [
        { label: "Seated", count: totalSeated, color: "#4A7FE5" },
        { label: "Available", count: Math.max(0, totalCapacity - totalSeated), color: T.green },
    ];

    /* ── MOBILE LAYOUT ── */
    if (mobile) {
        return (
            <>
                {/* Weekly Trend — hidden for Today, visible for all other date filters */}
                {dateContext !== "today" && (hasData(weeklyTrend) ? (
                    <Card mobile>
                        <SecTitle sub="7-day reservation volume & guests" mobile>Weekly Trend</SecTitle>
                        <div className="resv-analytics__chart-h100">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={weeklyTrend} barGap={1}>
                                    <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} />
                                    <YAxis hide />
                                    <Tooltip content={<ATooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 9, fontFamily: T.font }} />
                                    <Bar dataKey="reservations" name="Reservations" radius={[3, 3, 0, 0]} maxBarSize={18} fill={T.red} />
                                    <Bar dataKey="guests" name="Guests" radius={[3, 3, 0, 0]} maxBarSize={12} fill={T.orange} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                ) : (
                    <Card mobile>
                        <SecTitle mobile>Weekly Trend</SecTitle>
                        <SectionEmpty kpiName="weekly trend" minHeight={120} />
                    </Card>
                ))}


                {/* Turn Time Accuracy — commented out: requires completed reservation lifecycle data (actual_dinein_time, actual_dineout_time, estimate_seconds) */}

                {/* Channel Donut — always show */}
                <Card mobile>
                    <SecTitle sub="Booking source distribution" mobile>Reservation Channels</SecTitle>
                    {hasData(channelData) ? (
                        <div className="resv-analytics__donut-wrap resv-analytics__donut-wrap--mobile">
                            <div className="resv-analytics__donut-chart resv-analytics__donut-chart--mobile">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={channelData} cx="50%" cy="50%" innerRadius={34} outerRadius={50} dataKey="value" strokeWidth={0}>
                                            {channelData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="resv-analytics__legend">
                                {channelData.slice(0, 6).map((e, i) => (
                                    <div key={i} className="resv-analytics__legend-item">
                                        <span className="resv-analytics__legend-dot" style={{ background: e.color }} />
                                        <span className="resv-analytics__legend-name resv-analytics__legend-name--mobile">{e.name}</span>
                                        <span className="resv-analytics__legend-pct">{Math.round((e.value / chTotal) * 100)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <SectionEmpty kpiName="reservation channels" minHeight={120} />
                    )}
                </Card>

                {/* Floor Status — commented out: no real-time table status API available yet
                {(totalSeated > 0 || totalCapacity > 0) && (
                    <Card mobile>
                        <SecTitle sub={`${totalCapacity} total capacity`} mobile>Floor Status</SecTitle>
                        <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
                            {floorSegs.map((s, i) => <div key={i} style={{ flex: s.count || 1, background: s.color, opacity: 0.7 }} />)}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            {floorSegs.map((s, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{s.count}</div>
                                        <div style={{ fontSize: 8, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{s.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 10, padding: "8px 12px", background: T.accentSoft, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>{seatPct}%</span>
                            <span style={{ fontSize: 10, color: T.t2 }}>seating efficiency</span>
                        </div>
                    </Card>
                )}
                */}

                {/* Hourly Covers — always show */}
                <Card mobile>
                    <SecTitle sub="Expected covers by hour" mobile>Shift Forecast</SecTitle>
                    {hasData(hcData) ? (
                        <>
                        <div className="resv-analytics__chart-h100">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={hcData} margin={{ top: 14, right: 4, left: 0, bottom: 6 }}>
                                    <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                                    <XAxis
                                        dataKey="hour"
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tickMargin={4}
                                        height={42}
                                        tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font, angle: -45, textAnchor: 'end' }}
                                    />
                                    <YAxis hide />
                                    <Tooltip content={<ATooltip />} />
                                    {showAvgHc && (
                                        <ReferenceLine y={avgHc} stroke={T.t3} strokeDasharray="4 3" ifOverflow="extendDomain" label={{ value: `Avg: ${avgHcDisplay}`, position: "insideTopRight", fill: T.t2, fontSize: 8, fontWeight: 500 }} />
                                    )}
                                    <Bar dataKey="covers" name="Covers" radius={[3, 3, 0, 0]} maxBarSize={18}>
                                        {hcData.map((_, i) => <Cell key={i} fill={i === peakHc ? T.green : "rgba(22,163,74,0.4)"} />)}
                                        <LabelList dataKey="covers" content={(props: any) => {
                                            const { x, y, width, height, index, value } = props;
                                            if (index !== peakHc || !Number(value)) return null;
                                            const bw = Number(width);
                                            const bh = Number(height);
                                            const cx = Number(x) + bw / 2;
                                            const showInside = bw >= 18 && bh >= 20;
                                            return (
                                                <g>
                                                    <text x={cx} y={Number(y) - 3} textAnchor="middle" fill={T.green} fontSize={9} fontWeight={700}>{value}</text>
                                                </g>
                                            );
                                        }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        {peakHcValue > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: `1px solid ${T.divider}`, fontSize: 10, color: T.t3, gap: 6 }}>
                                <span>Peak: <span style={{ color: T.green, fontWeight: 600 }}>{hcData[peakHc]?.hour ?? "--"}</span>{` — ${peakHcValue}`}</span>
                                <span>Avg: <span style={{ color: T.t1, fontWeight: 600 }}>{avgHc > 0 ? avgHcDisplay : "--"}</span></span>
                                <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{totalHc.toLocaleString()}</span></span>
                            </div>
                        )}
                        </>
                    ) : (
                        <SectionEmpty kpiName="shift forecast" minHeight={120} />
                    )}
                </Card>

                {/* Reservation Heatmap */}
                <Card mobile>
                    <SecTitle sub="Density by day and hour" mobile>Reservation Heatmap</SecTitle>
                    {hasData(heatmap) && hmHours.length > 0 ? (
                        <div className="resv-analytics__heatmap-wrap resv-analytics__heatmap-wrap--mobile">
                            <div className="resv-analytics__heatmap-days resv-analytics__heatmap-days--mobile">
                                {hmDays.map(d => <div key={d} className="resv-analytics__heatmap-day-label resv-analytics__heatmap-day-label--mobile">{DAY_NAMES[d]}</div>)}
                            </div>
                            <div className="resv-analytics__heatmap-body resv-analytics__heatmap-body--mobile">
                                <div className="resv-analytics__heatmap-hour-row">
                                    {hmHourLabels.map((h, i) => <div key={i} className="resv-analytics__heatmap-hour-label resv-analytics__heatmap-hour-label--mobile">{h}</div>)}
                                </div>
                                {hmDays.map(day => (
                                    <div key={day} className="resv-analytics__heatmap-hour-row">
                                        {hmHours.map((hour, ci) => {
                                            const v = hmGrid[`${day}-${hour}`] || 0;
                                            const intensity = v / hmMax;
                                            return (
                                                <div key={ci} className="resv-analytics__heatmap-cell resv-analytics__heatmap-cell--mobile" style={{
                                                    background: `rgba(210,58,59,${Math.max(0.06, intensity * 0.65)})`,
                                                    color: intensity >= 0.5 ? "#fff" : "transparent",
                                                }}>{intensity >= 0.5 ? `${Math.round(intensity * 100)}%` : ""}</div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <SectionEmpty kpiName="" minHeight={160} />
                    )}
                </Card>
            </>
        );
    }

    /* ── DESKTOP LAYOUT ── */
    return (
        <>
            {/* Row 1: Weekly Trend — hidden for Today, visible for all other date filters */}
            {dateContext !== "today" && (
                <div className="resv-analytics__flex-row">
                    {hasData(weeklyTrend) ? (
                        <Card style={{ flex: 2 }}>
                            <SecTitle sub="7-day reservation volume & guests">Weekly Trend</SecTitle>
                            <div className="resv-analytics__chart-h180">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={weeklyTrend} barGap={2}>
                                        <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} />
                                        <YAxis hide />
                                        <Tooltip content={<ATooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 11, fontFamily: T.font }} />
                                        <Bar dataKey="reservations" name="Reservations" radius={[3, 3, 0, 0]} maxBarSize={24} fill={T.red} />
                                        <Bar dataKey="guests" name="Guests" radius={[3, 3, 0, 0]} maxBarSize={16} fill={T.orange} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    ) : <Card style={{ flex: 2 }}><SecTitle>Weekly Trend</SecTitle><SectionEmpty kpiName="weekly trend" minHeight={200} /></Card>}
                </div>
            )}

            {/* Row 2: Channel Donut (flex:1) + Shift Forecast (flex:1.5) */}
            <div className="resv-analytics__flex-row">
                <Card style={{ flex: 1 }}>
                    <SecTitle sub="Booking source distribution">Reservation Channels</SecTitle>
                    {hasData(channelData) ? (
                        <div className="resv-analytics__donut-wrap resv-analytics__donut-wrap--desktop">
                            <div className="resv-analytics__donut-chart resv-analytics__donut-chart--desktop">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={channelData} cx="50%" cy="50%" innerRadius={42} outerRadius={62} dataKey="value" strokeWidth={0}>
                                            {channelData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="resv-analytics__legend">
                                {channelData.slice(0, 6).map((e, i) => (
                                    <div key={i} className="resv-analytics__legend-item">
                                        <span className="resv-analytics__legend-dot" style={{ background: e.color }} />
                                        <span className="resv-analytics__legend-name resv-analytics__legend-name--desktop">{e.name}</span>
                                        <span className="resv-analytics__legend-pct">{Math.round((e.value / chTotal) * 100)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <SectionEmpty kpiName="reservation channels" minHeight={140} />
                    )}
                </Card>

                <Card style={{ flex: 5 }}>
                    <SecTitle sub="Expected covers by hour">Shift Forecast</SecTitle>
                    {hasData(hcData) ? (
                        <>
                        <div className="resv-analytics__chart-h160">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={hcData} margin={{ top: 18, right: 4, left: 0, bottom: 8 }}>
                                    <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                                    <XAxis
                                        dataKey="hour"
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tickMargin={4}
                                        height={46}
                                        tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font, angle: -45, textAnchor: 'end' }}
                                    />
                                    <YAxis hide />
                                    <Tooltip content={<ATooltip />} />
                                    {showAvgHc && (
                                        <ReferenceLine y={avgHc} stroke={T.t3} strokeDasharray="4 3" ifOverflow="extendDomain" label={{ value: `Avg: ${avgHcDisplay}`, position: "insideTopRight", fill: T.t2, fontSize: 10, fontWeight: 500 }} />
                                    )}
                                    <Bar dataKey="covers" name="Covers" radius={[3, 3, 0, 0]} maxBarSize={24}>
                                        {hcData.map((_, i) => <Cell key={i} fill={i === peakHc ? T.green : "rgba(22,163,74,0.4)"} />)}
                                        <LabelList dataKey="covers" content={(props: any) => {
                                            const { x, y, width, height, index, value } = props;
                                            if (index !== peakHc || !Number(value)) return null;
                                            const bw = Number(width);
                                            const bh = Number(height);
                                            const cx = Number(x) + bw / 2;
                                            const showInside = bw >= 18 && bh >= 22;
                                            return (
                                                <g>
                                                    <text x={cx} y={Number(y) - 4} textAnchor="middle" fill={T.green} fontSize={10} fontWeight={700}>{value}</text>
                                                </g>
                                            );
                                        }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        {peakHcValue > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, fontSize: 11, color: T.t3, gap: 6 }}>
                                <span>Peak: <span style={{ color: T.green, fontWeight: 600 }}>{hcData[peakHc]?.hour ?? "--"}</span>{` — ${peakHcValue}`}</span>
                                <span>Avg: <span style={{ color: T.t1, fontWeight: 600 }}>{avgHc > 0 ? avgHcDisplay : "--"}</span></span>
                                <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{totalHc.toLocaleString()}</span></span>
                            </div>
                        )}
                        </>
                    ) : (
                        <SectionEmpty kpiName="shift forecast" minHeight={160} />
                    )}
                </Card>
            </div>

            {/* Row 4: Heatmap — full width, always show on desktop */}
            <Card>
                <SecTitle sub="Density by day and hour">Reservation Heatmap</SecTitle>
                {hasData(heatmap) && hmHours.length > 0 ? (
                    <div className="resv-analytics__heatmap-wrap">
                        <div className="resv-analytics__heatmap-days">
                            {hmDays.map(d => <div key={d} className="resv-analytics__heatmap-day-label">{DAY_NAMES[d]}</div>)}
                        </div>
                        <div className="resv-analytics__heatmap-body">
                            <div className="resv-analytics__heatmap-hour-row">
                                {hmHourLabels.map((h, i) => <div key={i} className="resv-analytics__heatmap-hour-label">{h}</div>)}
                            </div>
                            {hmDays.map(day => (
                                <div key={day} className="resv-analytics__heatmap-hour-row">
                                    {hmHours.map((hour, ci) => {
                                        const v = hmGrid[`${day}-${hour}`] || 0;
                                        const intensity = v / hmMax;
                                        return (
                                            <div key={ci} className="resv-analytics__heatmap-cell" style={{
                                                background: `rgba(210,58,59,${Math.max(0.06, intensity * 0.65)})`,
                                                color: intensity >= 0.5 ? "#fff" : "transparent",
                                            }}>{intensity >= 0.5 ? `${Math.round(intensity * 100)}%` : ""}</div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <SectionEmpty kpiName="" minHeight={240} />
                )}
            </Card>
        </>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED CONTENT COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

function ReservationContent({ mobile }: any) {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? "", [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const dispatch = useAppDispatch();
    const {
        todayNewDetailsLoading,
        todayNewDetails,
        todayNewDetailsTotalElements,
        todayNewDetailsTotalPages,
        todayNewDetailsFailure,
    } = useAppSelector((state) => state.reservationReport);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    // Reservations legitimately selects future dates, so the shared filter must not clamp a
    // forward-looking range back to today (GLOBAL-DP-5 midnight-drift guard).
    const [activeDate, setActiveDate] = useSharedReportDateFilter("Today", { allowFutureDates: true });

    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDate, today, yesterday, storeTz);
        return range || { startDate: today, endDate: today };
    }, [activeDate, today, yesterday, storeTz]);

    const dateContext = useMemo(() => getDateContext(dateRange, today, activeDate), [dateRange, today, activeDate]);

    // Table state
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(mobile ? 5 : 10);
    const [sortBy, setSortBy] = useState("resvTime");
    const [sortAsc, setSortAsc] = useState(true);
    const [sortTouched, setSortTouched] = useState(false);
    const [phoneVisible, setPhoneVisible] = useState(false);
    const [todayRetryTick, setTodayRetryTick] = useState(0);

    // Debounce search (used for past/future contexts)
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            if (dateContext !== "today") setPage(0);
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    // Today's table: dispatch Redux action (saga handles debounce + date adjustment)
    useEffect(() => {
        if (dateContext !== "today" || !locationId) return;
        dispatch(fetchTodayNewDetailsRequest({
            locationId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            page,
            size: pageSize,
            search,
            ...(sortTouched ? { sortBy, sortAsc } : {}),
        }));
    }, [dispatch, locationId, dateContext, dateRange.startDate, dateRange.endDate, page, pageSize, search, sortBy, sortAsc, sortTouched, todayRetryTick]);

    // Reset page on filter/sort change
    const handleStatusChange = useCallback((v) => { setStatusFilter(v); setPage(0); }, []);
    const handleSort = useCallback((col) => {
        if (sortBy === col) { setSortAsc(a => !a); }
        else { setSortBy(col); setSortAsc(true); }
        setSortTouched(true);
        setPage(0);
    }, [sortBy]);
    const handlePageSizeChange = useCallback((newSize) => { setPageSize(newSize); setPage(0); }, []);

    // Reset table state on date change
    useEffect(() => {
        setSearch("");
        setDebouncedSearch("");
        // For all contexts, show all by default (today combines Upcoming + Seated)
        setStatusFilter("");
        setPage(0);
        setSortBy("resvTime");
        setSortAsc(dateContext !== "past");
        setSortTouched(false);
    }, [activeDate, dateContext]);

    const tableParams = useMemo(() => ({
        page, size: pageSize, search: debouncedSearch, statusFilter, sortBy, sortAsc, sortTouched,
    }), [page, pageSize, debouncedSearch, statusFilter, sortBy, sortAsc, sortTouched]);

    const { kpiLoading, tableLoading, error, kpis, chartData, tableData, seatedTotal, chartError, tableError, retryChart, retryTable } = useReservationData(locationId, token, dateRange, tableParams, dateContext);

    // TopBar setup
    useEffect(() => {
        const props: any = {
            title: "Reservations",
            dateFilters: { value: activeDate, onChange: setActiveDate, options: mobile ? DATE_PRESETS_MOBILE : DATE_PRESETS_DESKTOP, allowFutureDates: true },
        };
        if (!mobile) {
            props.breadcrumbs = "Reports";
            props.onExport = () => {};
        }
        setTopBarProps(props);
    }, [setTopBarProps, activeDate, mobile]);

    // Table title and headers based on context
    const tableTitle = dateContext === "today"
        ? "Today's Reservations"
        : dateContext === "future"
            ? "Upcoming Reservations"
            : "Completed Reservations";

    const tableHeaders = dateContext === "today" ? TABLE_HEADERS_TODAY : TABLE_HEADERS_OVERVIEW;

    // For today: build status options with counts from API totalElements
    const statusOptions = useMemo(() => {
        if (dateContext === "today") {
            // We know the upcoming total from the current table response when filter is Upcoming,
            // and seatedTotal from the separate lightweight fetch
            const upcomingTotal = statusFilter !== "Seated" ? tableData.totalElements : null;
            const seatedCount = seatedTotal;
            return [
                { label: "Upcoming", value: "Upcoming", count: upcomingTotal },
                { label: "Seated", value: "Seated", count: seatedCount },
            ];
        }
        return OVERVIEW_STATUS_OPTIONS.map(s => ({ label: s, value: s === "All" ? "" : s, count: null }));
    }, [dateContext, statusFilter, tableData.totalElements, seatedTotal]);

    // ── Upcoming Covers (separate API call) — hidden ──
    // const [upcomingCovers, setUpcomingCovers] = useState<any>(null);
    // useEffect(() => {
    //     if (!locationId || !token) return;
    //     const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
    //     getUpcomingReservationOverview(opts)
    //         .then(res => {
    //             const data = unwrapRes(res);
    //             const covers = data?.totalReservation ?? data?.totalReservations ?? data?.totalGuests ?? null;
    //             setUpcomingCovers(covers);
    //         })
    //         .catch(() => setUpcomingCovers(null));
    // }, [locationId, token, dateRange.startDate, dateRange.endDate]);

    // ── Analytics data ──
    const [analytics, setAnalytics] = useState<any>({});
    const [analyticsLoading, setAnalyticsLoading] = useState(true);

    useEffect(() => {
        if (!locationId || !token) { setAnalyticsLoading(false); return; }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        const liveAnalyticsOpts = dateContext === "today" ? { ...opts, type: "live" } : opts;
        setAnalyticsLoading(true);
        Promise.allSettled([
            getReservationTurnTimeAccuracy(opts),
            getReservationChannelDistribution(liveAnalyticsOpts),
            getReservationHourlyCovers(liveAnalyticsOpts),
            getReservationHeatmap(liveAnalyticsOpts),
            getReservationNoShowSummary(opts),
            getReservationAvgWaitTime(opts),
            getReservationSeatingEfficiency(opts),
            getReservationPartySizeDelta(opts),
        ]).then(([tt, ch, hc, hm, ns, wt, se, ps]) => {
            const v = (r: any) => r.status === "fulfilled" ? r.value : null;
            const unwrap = (d: any) => { const raw = d?.metaDataInfo?.data ?? d?.data ?? d; return raw; };
            console.log('[ReservationReport] getReservationHourlyCovers (/reservations/analytics/hourly-covers):', JSON.stringify(v(hc), null, 2));
            const analyticsData = {
                turnTime: Array.isArray(unwrap(v(tt))) ? unwrap(v(tt)) : [],
                channels: Array.isArray(unwrap(v(ch))) ? unwrap(v(ch)) : [],
                hourlyCovers: Array.isArray(unwrap(v(hc))) ? unwrap(v(hc)) : [],
                heatmap: Array.isArray(unwrap(v(hm))) ? unwrap(v(hm)) : [],
                noShow: unwrap(v(ns)) || {},
                waitTime: unwrap(v(wt)) || {},
                seating: unwrap(v(se)) || {},
                partySize: unwrap(v(ps)) || {},
            };

            setAnalytics(analyticsData);
            setAnalyticsLoading(false);
        });
    }, [locationId, token, dateRange.startDate, dateRange.endDate, dateContext]);

    // Derive 6 KPI cards matching reference design — mapped from real API data
    const kpiCards = useMemo(() => {
        const totalRes = kpis ? fmtNum(pick(kpis, "totalReservations", "totalReservation", "total") ?? 0) : "--";
        const totalGuests = kpis ? fmtNum(pick(kpis, "totalGuests") ?? 0) : "--";

        // const noShowRate = analytics.noShow?.noShowRate;
        // const noShowVal = noShowRate != null ? `${Number(noShowRate).toFixed(1)}%` : "--";
        // const noShowDelta = analytics.noShow?.deltaPercent ?? null;

        // Compute AVG PARTY SIZE from totalGuests / totalReservations (fallback when analytics API has no data)
        const rawTotalRes = num(pick(kpis, "totalReservations", "totalReservation", "total"));
        const rawTotalGuests = num(pick(kpis, "totalGuests"));
        // Always compute from totalGuests / totalReservations (analytics API may return 0 or empty)
        const avgParty = rawTotalRes > 0 ? rawTotalGuests / rawTotalRes : null;
        const avgPartyVal = avgParty != null ? Number(avgParty).toFixed(1) : "--";
        const partyDelta = analytics.partySize?.deltaPercent ?? null;

        const waitMin = analytics.waitTime?.avgWaitTimeMinutes;
        const waitVal = waitMin != null ? `${Math.round(Number(waitMin))} min` : "--";
        const waitDelta = analytics.waitTime?.deltaPercent ?? null;

        const seatEff = analytics.seating?.efficiencyPercent;
        const seatVal = seatEff != null ? `${Math.round(Number(seatEff))}%` : "--";
        const seatDelta = analytics.seating?.deltaPercent ?? null;

        return [
            { key: "totalRes", label: "TOTAL RESERVATIONS", value: totalRes, delta: null, emoji: "\ud83d\udccb" },
            // { key: "noShow", label: "NO-SHOW RATE", value: noShowVal, delta: noShowDelta, emoji: "\ud83d\udc7b" },  // hidden: no api support
            // { key: "avgParty", label: "AVG PARTY SIZE", value: avgPartyVal, delta: null, emoji: "\ud83d\udc65" },
            // { key: "covers", label: "UPCOMING COVERS", value: upcomingCovers != null ? fmtNum(upcomingCovers) : "--", delta: null, emoji: "\ud83e\ude91" },
            // { key: "waitTime", label: "AVG WAIT TIME", value: waitVal, delta: waitDelta, emoji: "\u23f1" },  // hidden: no backend data
            // { key: "seatEff", label: "SEATING EFFICIENCY", value: seatVal, delta: seatDelta, emoji: "\ud83d\udcd0" },  // hidden: no backend data
        ];
    }, [kpis, analytics]);

    if (!locationId) return <div className="resv-state resv-state--no-location">Select a location to view Reservations.</div>;
    if (kpiLoading || analyticsLoading) return mobile ? <ReservationMobileShimmer /> : <ReservationDesktopShimmer />;
    if (error) return <div className="resv-state resv-state--error">{error}</div>;

    return (
        <div className={`resv-content ${mobile ? "resv-content--mobile" : "resv-content--desktop"}`}>
            {/* 6 KPI Cards */}
            {mobile ? (
                <>
                    {[kpiCards.slice(0, 3), kpiCards.slice(3, 6)].map((row, ri) => (
                        <div key={ri} className="resv-content__kpi-grid" style={{ gridTemplateColumns: `repeat(${row.length},1fr)` }}>
                            {row.map(k => <KPIMobile key={k.key} label={k.label} value={k.value} delta={k.delta} />)}
                        </div>
                    ))}
                </>
            ) : (
                <div className="resv-content__kpi-row">
                    {kpiCards.map(k => <KPIDesktop key={k.key} label={k.label} value={k.value} delta={k.delta} emoji={k.emoji} />)}
                </div>
            )}

            {/* Analytics charts — matching reference layout */}
            {chartError ? (
                <div className="rpt-card" style={{ padding: 0, borderRadius: 14, overflow: "hidden" }}>
                    <SectionError kpiName="reservation volume" onRetry={retryChart} size="medium" minHeight={240} />
                </div>
            ) : (!analyticsLoading && <AnalyticsSection analytics={analytics} weeklyData={chartData} mobile={mobile} dateContext={dateContext} />)}

            {/* Reservation Table */}
            {(() => {
                const isToday = dateContext === "today";
                // For today context: use Redux data; normalize liveTodayStatus → status
                const effectiveRows = isToday
                    ? todayNewDetails.map((r) => ({
                        ...r,
                        status: r.liveTodayStatus ?? r.status ?? "",
                    }))
                    : tableData.content;
                const effectiveTotalElements = isToday ? todayNewDetailsTotalElements : tableData.totalElements;
                const effectiveTotalPages = isToday ? todayNewDetailsTotalPages : tableData.totalPages;
                const effectiveLoading = isToday ? todayNewDetailsLoading : tableLoading;
                const effectiveError = isToday ? todayNewDetailsFailure : tableError;

                return (
                    <ReservationTable
                        title={tableTitle}
                        headers={tableHeaders}
                        rows={effectiveRows}
                        totalElements={effectiveTotalElements}
                        totalPages={effectiveTotalPages}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        search={search}
                        onSearchChange={setSearch}
                        statusFilter={statusFilter}
                        onStatusFilterChange={handleStatusChange}
                        statusOptions={statusOptions}
                        sortBy={sortBy}
                        sortAsc={sortAsc}
                        sortTouched={sortTouched}
                        onSort={handleSort}
                        loading={effectiveLoading}
                        mobile={mobile}
                        phoneVisible={phoneVisible}
                        onTogglePhone={() => setPhoneVisible(v => !v)}
                        error={effectiveError}
                        onRetry={isToday ? () => setTodayRetryTick(n => n + 1) : retryTable}
                    />
                );
            })()}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP INNER CONTENT
   ═══════════════════════════════════════════════════════════════════════════ */
function ReservationDesktopContent() {
    return <ReservationContent mobile={false} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE SCREEN
   ═══════════════════════════════════════════════════════════════════════════ */
function MobileReservationScreen() {
    const [refreshNonce, setRefreshNonce] = useState(0);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({
        onRefresh: useCallback(() => setRefreshNonce(n => n + 1), []),
    });
    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} className="resv-mobile-screen">
                <ReservationContent key={refreshNonce} mobile={true} />
            </div>
            <BottomNav />
        </Phone>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function ReservationReport() {
    const isDesktop = useIsDesktop();
    useSwipeBack();
    return isDesktop ? (
        <DesktopLayout>
            <ReservationDesktopContent />
        </DesktopLayout>
    ) : (
        <MobileReservationScreen />
    );
}

